import {
  FOLGEAKTIONSNAME,
  type Folgeaktionszeile,
  type Nachrichtzeile,
  type Sicherheit,
  type Versandzeile,
  type Vorgangsstatus,
  type Zuordnungsstand,
} from '../api/support';

/**
 * DER NÄCHSTE SCHRITT IM VORGANG (Auftrag CRM-196).
 *
 * Wunsch von Ulf (11.10.2026): „strukturiere die Postfachseite etwas um. wenn
 * ein Vorgang offen ist, ist die Trennung zwischen den einzelnen
 * Emailverläufen nicht klar erkennbar, ebenso der Prozess, welcher schritt
 * als nächstes folgt."
 *
 * Fünf Schritte: Eingang → Zuordnen → Antworten → Warten/Folgeaktion → Lösen.
 * Jeder ist für sich erledigt oder nicht; der aktuelle ist der erste, der es
 * nicht ist. Abgeleitet wird nur aus dem, was die Maske schon hat — Status
 * (SUP-17), Zuordnung (SUP-07, SUP-09), Verlauf, Versandstand (SUP-19) und
 * Folgeaktionen (SUP-23). Rein: keine Uhr, kein Aufruf, kein Zustand.
 */

export type Schrittschluessel = 'eingang' | 'zuordnen' | 'antworten' | 'warten' | 'loesen';

export type Schrittstand = 'erledigt' | 'aktuell' | 'offen';

export type Schritt = {
  readonly schluessel: Schrittschluessel;
  readonly name: string;
  readonly stand: Schrittstand;
};

/** Wohin der Knopf am nächsten Schritt führt; die Maske setzt es um. */
export type Schritthandlung =
  | { readonly art: 'uebernehmen'; readonly beschriftung: string }
  | { readonly art: 'zuordnung'; readonly beschriftung: string }
  | { readonly art: 'antworten'; readonly beschriftung: string }
  | { readonly art: 'folgeaktionen'; readonly beschriftung: string }
  | { readonly art: 'status'; readonly beschriftung: string; readonly status: Vorgangsstatus };

export type NaechsterSchritt = {
  readonly schritte: readonly Schritt[];
  /** 1-basiert; null, wenn der Vorgang erledigt ist. */
  readonly nummer: number | null;
  /** Der Satz hinter „Als Nächstes:" — oder hinter „Erledigt:". */
  readonly satz: string;
  /** Ein Zusatz, etwa der Grund eines gescheiterten Versands. */
  readonly hinweis: string | null;
  readonly handlung: Schritthandlung | null;
  readonly erledigt: boolean;
};

export type Schrittlage = {
  readonly status: Vorgangsstatus;
  readonly kunde: string | null;
  readonly zuordnung: Zuordnungsstand;
  readonly sicherheit: Sicherheit | null;
  readonly nachrichten: readonly Pick<Nachrichtzeile, 'id' | 'richtung' | 'art' | 'eingegangenAm'>[];
  /** SUP-19: je ausgehender Nachricht der Stand im Postausgang. */
  readonly versand: Readonly<Record<string, Versandzeile>>;
  readonly folgeaktionen: readonly Pick<Folgeaktionszeile, 'art' | 'stand'>[];
};

const NAMEN: Readonly<Record<Schrittschluessel, string>> = {
  eingang: 'Eingang',
  zuordnen: 'Zuordnen',
  antworten: 'Antworten',
  warten: 'Warten/Folgeaktion',
  loesen: 'Lösen',
};

const REIHE: readonly Schrittschluessel[] = ['eingang', 'zuordnen', 'antworten', 'warten', 'loesen'];

/**
 * Wer zuletzt gesprochen hat. Abwesenheitsnotiz, automatische Antwort und
 * Zustellbericht verlangen keine Antwort; eine eingehende Nachricht zählt nur
 * als `nachricht` (oder Massenversand, den der Bearbeiter übernommen hat).
 */
function letzteGespraechsnachricht(lage: Schrittlage): Schrittlage['nachrichten'][number] | null {
  let letzte: Schrittlage['nachrichten'][number] | null = null;
  for (const n of lage.nachrichten) {
    const zaehlt = n.richtung === 'ausgehend' || n.art === 'nachricht' || n.art === 'massenmail';
    if (!zaehlt) continue;
    // Gleiche Zeit: die spätere in der Liste gilt (der Verlauf ist älteste zuerst).
    if (letzte === null || Date.parse(n.eingegangenAm) >= Date.parse(letzte.eingegangenAm)) letzte = n;
  }
  return letzte;
}

/** SUP-09: Eine automatische Zuordnung, die nicht sicher ist, wartet auf den Blick eines Menschen. */
function zuordnungOffen(lage: Schrittlage): boolean {
  if (lage.kunde === null) return true;
  return lage.zuordnung === 'automatisch' && lage.sicherheit !== null && lage.sicherheit !== 'sicher';
}

export function naechsterSchritt(lage: Schrittlage): NaechsterSchritt {
  // SUP-17: gelöst und geschlossen drängen nicht — alles erledigt. Gelöst lässt sich
  // wieder öffnen (zurück in Bearbeitung, CRM-201); geschlossen bleibt ohne Knopf (E-19 offen).
  if (lage.status === 'geloest' || lage.status === 'geschlossen') {
    return {
      schritte: REIHE.map((s) => ({ schluessel: s, name: NAMEN[s], stand: 'erledigt' })),
      nummer: null,
      satz: lage.status === 'geloest' ? 'Der Vorgang ist gelöst.' : 'Der Vorgang ist geschlossen.',
      hinweis: null,
      handlung: lage.status === 'geloest'
        ? { art: 'status', beschriftung: 'Wieder öffnen', status: 'in_bearbeitung' }
        : null,
      erledigt: true,
    };
  }

  const letzte = letzteGespraechsnachricht(lage);
  const versand = letzte !== null && letzte.richtung === 'ausgehend' ? lage.versand[letzte.id] : undefined;
  // SUP-19: Eine aufgegebene Antwort ist beim Kunden nie angekommen.
  const versandGescheitert = versand?.stand === 'aufgegeben';
  const versandhinweis = versand?.stand === 'bereit' || versand?.stand === 'laeuft' ? 'Die Antwort wartet noch auf den Versand.' : null;
  const offeneFolgeaktion = lage.folgeaktionen.find((f) => f.stand === 'angestossen');
  const wartet = lage.status === 'wartet_auf_kunde' || lage.status === 'wartet_intern';

  const erledigt: Readonly<Record<Schrittschluessel, boolean>> = {
    eingang: lage.status !== 'neu',
    zuordnen: !zuordnungOffen(lage),
    antworten: letzte !== null && letzte.richtung === 'ausgehend' && !versandGescheitert,
    // Warten ist vorbei, wenn nichts mehr angestoßen ist und niemand mehr wartet.
    warten: offeneFolgeaktion === undefined && !wartet,
    loesen: false,
  };

  const aktuell = REIHE.find((s) => !erledigt[s]) ?? 'loesen';
  const schritte = REIHE.map((s): Schritt => ({
    schluessel: s,
    name: NAMEN[s],
    stand: s === aktuell ? 'aktuell' : erledigt[s] ? 'erledigt' : 'offen',
  }));
  const nummer = REIHE.indexOf(aktuell) + 1;
  const mit = (satz: string, handlung: Schritthandlung | null, hinweis: string | null = null): NaechsterSchritt => ({
    schritte, nummer, satz, hinweis, handlung, erledigt: false,
  });

  switch (aktuell) {
    case 'eingang':
      // SUP-18: Übernehmen setzt einen neuen Vorgang in Bearbeitung.
      return mit('Vorgang übernehmen und in Bearbeitung nehmen.', { art: 'uebernehmen', beschriftung: 'Vorgang übernehmen' });
    case 'zuordnen':
      if (lage.kunde !== null) {
        return mit('Zuordnung zum Kunden prüfen.', { art: 'zuordnung', beschriftung: 'Zuordnung öffnen' });
      }
      return mit(
        'Kontakt oder Organisation zuordnen.',
        { art: 'zuordnung', beschriftung: 'Zuordnung öffnen' },
        lage.zuordnung === 'ausstehend' ? 'Die automatische Zuordnung läuft mit dem nächsten Abruf.' : null,
      );
    case 'antworten':
      if (versandGescheitert) {
        return mit(
          'Die letzte Antwort ging nicht hinaus — erneut antworten.',
          { art: 'antworten', beschriftung: 'Antwort beginnen' },
          versand.fehler,
        );
      }
      return mit('Dem Kunden antworten.', { art: 'antworten', beschriftung: 'Antwort beginnen' });
    case 'warten':
      if (offeneFolgeaktion !== undefined) {
        return mit(
          `Folgeaktion „${FOLGEAKTIONSNAME[offeneFolgeaktion.art]}" abschließen.`,
          { art: 'folgeaktionen', beschriftung: 'Zu den Folgeaktionen' },
        );
      }
      return mit(
        lage.status === 'wartet_auf_kunde'
          ? 'Auf die Antwort des Kunden warten; ist das Anliegen erledigt, lösen.'
          : 'Die interne Klärung abwarten; ist das Anliegen erledigt, lösen.',
        { art: 'status', beschriftung: 'Als gelöst setzen', status: 'geloest' },
        versandhinweis,
      );
    case 'loesen':
      return mit(
        'Ist das Anliegen erledigt, den Vorgang lösen.',
        { art: 'status', beschriftung: 'Als gelöst setzen', status: 'geloest' },
        versandhinweis,
      );
  }
}
